import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { Store } from '@ngrx/store';
import { Channel } from '@pixacare/chat';
import { PublicUser } from '@pixacare/domain';
import { UserCardComponent } from '@shared/components/user-card/user-card.component';
import { selectClientCode } from '@shared/store/router/router.selectors';
import { TuiInput } from '@taiga-ui/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { UserHttpService } from 'src/app/services/http/user.http.service';
import { ChannelPreviewComponent } from '../channel-preview/channel-preview.component';
import { ChatService } from '../chat.service';
import { MessageGroupService } from '../message-group.service';
import { ChannelNamePipe } from '../pipes/channel-name.pipe';
import { ReceiverListComponent } from '../receiver-list/receiver-list.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

@Component({
  selector: 'pxc-receivers-picker',
  imports: [
    EmptyStateComponent,
    CommonModule,
    TuiInput,
    ChannelPreviewComponent,
    ReactiveFormsModule,
    ReceiverListComponent,
    LoaderComponent,
    TranslocoDirective,
    UserCardComponent,
  ],
  providers: [ChatService, ChannelNamePipe, MessageGroupService],
  templateUrl: './receivers-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiversPickerComponent implements OnInit, OnChanges {
  private readonly chatService = inject(ChatService);
  private readonly userService = inject(UserHttpService);
  private readonly clientCode$ = inject(Store).select(selectClientCode);

  @Input() selectedUsers: PublicUser[] = [];
  @Input() selectedChannels: Channel[] = [];
  @Input() selectedMailAddresses: string[] = [];
  @Output() private readonly selectedUsersChange = new EventEmitter<PublicUser[]>();
  @Output() private readonly selectedChannelsChange = new EventEmitter<Channel[]>();
  @Output() private readonly selectedMailAddressesChange = new EventEmitter<string[]>();

  readonly searchInput = new FormControl('', [Validators.email]);

  channels$: Observable<Channel[]>;
  users$: Observable<PublicUser[]>;

  private readonly searchDefaultValue = '';
  private search$ = this.searchInput.valueChanges.pipe(
    startWith(this.searchDefaultValue),
    debounceTime(300),
    distinctUntilChanged(),
  );

  private readonly selectedUsers$ = new BehaviorSubject<PublicUser[]>([]);
  private readonly selectedChannels$ = new BehaviorSubject<Channel[]>([]);

  ngOnInit(): void {
    this.channels$ = combineLatest([
      this.search$.pipe(switchMap((search) => this.chatService.searchChannels$({ search }))),
      this.selectedChannels$,
    ]).pipe(
      map(([channels, selectedChannels]) =>
        channels.filter((channel) => !selectedChannels.some((selectedChannel) => selectedChannel?.id === channel.id)),
      ),
    );

    this.users$ = combineLatest([
      this.search$.pipe(
        switchMap((search) =>
          search?.length
            ? this.userService.searchUsers(search)
            : this.clientCode$.pipe(
                switchMap((clientCode) => (clientCode ? this.userService.suggestedUsers(clientCode) : of([]))),
              ),
        ),
      ),
      this.selectedUsers$,
    ]).pipe(
      map(([users, selectedUsers]) =>
        users.filter((user) => !selectedUsers.some((selectedUser) => selectedUser?.id === user.id)),
      ),
    );
  }

  ngOnChanges({ selectedChannels, selectedUsers }: SimpleChanges): void {
    if (selectedChannels) {
      this.selectedChannels$.next(selectedChannels.currentValue);
    }

    if (selectedUsers) {
      this.selectedUsers$.next(selectedUsers.currentValue);
    }

    if (selectedChannels || selectedUsers) {
      this.resetSearch();
    }
  }

  submit(): void {
    if (!this.searchInput.valid) {
      return;
    }

    const email = this.searchInput.value;

    if (!email) {
      return;
    }

    if (this.selectedMailAddresses.some((selectedMailAddress) => selectedMailAddress === email)) {
      return;
    }

    this.selectedMailAddressesChange.emit([...this.selectedMailAddresses, email]);
    this.resetSearch();
  }

  selectChannel(channel: Channel): void {
    if (this.selectedChannels.some((selectedChannel) => selectedChannel?.id === channel.id)) {
      return;
    }
    this.selectedChannelsChange.emit([...this.selectedChannels, channel]);
  }

  selectUser(user: PublicUser): void {
    if (this.selectedUsers.some((selectedUser) => selectedUser?.id === user.id)) {
      return;
    }
    this.selectedUsersChange.emit([...this.selectedUsers, user]);
  }

  removeChannel(channel: Channel): void {
    this.selectedChannelsChange.emit(this.selectedChannels.filter((c) => c !== channel));
  }

  removeUser(user: PublicUser): void {
    this.selectedUsersChange.emit(this.selectedUsers.filter((u) => u !== user));
  }

  removeMailAddress(mailAddress: string): void {
    this.selectedMailAddressesChange.emit(this.selectedMailAddresses.filter((m) => m !== mailAddress));
  }

  resetSearch(): void {
    this.searchInput.setValue(this.searchDefaultValue);
  }
}
